import { Card, Skeleton } from "@mui/material";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import SWR_KEYS from "@/constants/swr-keys";
import useCreateClosedDays from "@/hooks/requests/closed-days/use-create-closed-days";
import useGetClosedDays from "@/hooks/requests/closed-days/use-get-closed-days";
import useRemoveClosedDays from "@/hooks/requests/closed-days/use-remove-closed-days";
import useUpdateClosedDays from "@/hooks/requests/closed-days/use-update-closed-days";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import usePagination from "@/hooks/use-pagination";
import { IClosedDays, IClosedDaysInput } from "@/interfaces/closed-days.interface";
import { ConfigurationTabMessages } from "@/messages/customer/configuration-tab.messages";

import ClosedDaysDialog from "./ClosedDaysDialog";
import ClosedDaysTable from "./ClosedDaysTable";

type Props = {
  messages: ConfigurationTabMessages;
};

function ClosedDaysCard({ messages }: Props) {
  const { isVisible, handleClose, handleOpen, selected } = useDialog<IClosedDays>();
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const { data, isLoading, error } = useGetClosedDays({ pageNumber, pageSize });
  const { create, isLoading: isCreating } = useCreateClosedDays();
  const { update, isLoading: isUpdating } = useUpdateClosedDays();
  const { remove, isLoading: isRemoving } = useRemoveClosedDays();
  const { mutate } = useMutate();

  const onSuccess = () => {
    mutate(SWR_KEYS.getClosedDays);
    handleClose();
  };

  const onSubmit = async (formData: IClosedDaysInput) => {
    if (selected) {
      await update({
        _id: selected.id,
        formData,
        successMessage: messages.closedDaysUpdateSuccess,
        errorMessage: messages.closedDaysUpdateError,
        onSuccess,
      });
      return;
    }

    await create({
      formData,
      successMessage: messages.closedDaysCreateSuccess,
      errorMessage: messages.closedDaysCreateError,
      onSuccess,
    });
  };

  const onRemove = async (item: IClosedDays) => {
    await remove({
      _id: item.id,
      successMessage: messages.closedDaysDeleteSuccess,
      errorMessage: messages.closedDaysDeleteError,
      onSuccess: () => mutate(SWR_KEYS.getClosedDays),
    });
  };

  const isSaving = isCreating || isUpdating || isRemoving;

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDTypography variant="h5" mb={2}>
          {messages.closedDaysSection}
        </MDTypography>

        {isLoading && <Skeleton variant="rectangular" width="100%" height={120} />}

        {error && !isLoading && (
          <AlertBlock
            title={messages.closedDaysLoadErrorTitle}
            description={messages.closedDaysLoadErrorDescription}
            alt={messages.closedDaysLoadErrorTitle}
          />
        )}

        {!isLoading && !error && data && (
          <ClosedDaysTable
            items={data.items}
            messages={messages}
            data={data}
            pagination={pagination}
            onRemove={onRemove}
            onEdit={handleOpen}
            onOpenCreate={() => handleOpen()}
          />
        )}
      </MDBox>

      <ClosedDaysDialog
        isOpen={isVisible}
        onClose={handleClose}
        messages={messages}
        isLoading={isSaving}
        selected={selected}
        onSubmit={onSubmit}
      />
    </Card>
  );
}

export default ClosedDaysCard;
